import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactElement,
} from "react";
import { useTextDomainContext, useUser, useLang } from "@/app/providers";
import {
  useAddDeal,
  useDealById,
  useEditDeal,
  usePriceTypes,
  GET_DEALS_LIST,
  useBiddingZones,
} from "@/hooks";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Link, useNavigate, useParams } from "react-router";
import {
  convertDateToUserTimezone,
  formatDateForAPI,
  parseLocaleNumber,
  removeTimezone,
  sendSlackMessage,
  SLACK_CHANNELS,
} from "@/utils";
import {
  CurrencyDropdown,
  EnergyTypeDropdown,
  FilterBox,
  MeterDropdown,
  PriceTypeDropdownSimple,
  SingleDatePicker,
  TableBox,
  CommonDropdown,
  CustomersCountryDropdown,
  BuildingDropdown,
  LocaleNumberTextField,
} from "@/components";

interface User {
  timezone?: string;
  currentTeam?: string;
  currentSubsidiary?: string;
  currentTeamId?: string;
  currentSubsidiaryId?: string;
  countryCode?: string;
}

interface PriceType {
  value: string;
  price?: boolean;
  system?: boolean;
  epad?: boolean;
  profile?: boolean;
  markup?: boolean;
  commission?: boolean;
}

interface DealRow {
  Id?: string;
  building?: string;
  meter?: string;
  energyType?: string;
  fromDate?: string | Date;
  toDate?: string | Date;
  currency?: string;
  mainPriceType?: string;
  priceType?: string;
  pricePrice?: number | string;
  priceKwh?: number | string;
  priceShare?: number | string;
  systemType?: string;
  systemPrice?: number | string;
  systemKwh?: number | string;
  systemShare?: number | string;
  epadType?: string;
  epadPrice?: number | string;
  epadKwh?: number | string;
  epadShare?: number | string;
  profileType?: string;
  profilePrice?: number | string;
  profileKwh?: number | string;
  profileShare?: number | string;
  markupPrice?: number | string;
  markupShare?: number | string;
  commissionPrice?: number | string;
  commissionShare?: number | string;
  priceColumn?: string;
  zone?: string;
}

interface DefaultValues extends DealRow {
  building: string;
  meter: string;
  energyType: string;
  fromDate: Date;
  toDate: Date;
  currency: string;
  mainPriceType: string;
  priceType: string;
  pricePrice: number;
  priceKwh: number;
  priceShare: number;
  systemType: string;
  systemPrice: number;
  systemKwh: number;
  systemShare: number;
  epadType: string;
  epadPrice: number;
  epadKwh: number;
  epadShare: number;
  profileType: string;
  profilePrice: number;
  profileKwh: number;
  profileShare: number;
  markupPrice: number;
  markupShare: number;
  commissionPrice: number;
  commissionShare: number;
  priceColumn: string;
}

interface LocalFilters {
  buildingValue?: string;
  meterValue?: string;
  energyTypeValue?: string;
  fromDateValue?: Date;
  toDateValue?: Date;
  currencyValue?: string;
  mainPriceTypeValue?: string;
}

interface SlackErrorData {
  channel: string;
  type: string;
  heading: string;
  detail: string;
  fields: Array<{ title?: string; value?: string }>;
}

interface DealVariables {
  country?: string;
  zone?: string;
  customer?: string;
  subsidiary?: string;
  building: string;
  meter: string | number;
  energyType: string;
  fromDate: string;
  toDate: string;
  currency: string;
  mainPriceType: string;
  priceType: string;
  pricePrice: number;
  priceKwh: number;
  priceShare: number;
  systemType: string;
  systemPrice: number;
  systemKwh: number;
  systemShare: number;
  epadType: string;
  epadPrice: number;
  epadKwh: number;
  epadShare: number;
  profileType: string;
  profilePrice: number;
  profileKwh: number;
  profileShare: number;
  markupPrice: number;
  markupShare: number;
  commissionPrice: number;
  commissionShare: number;
  id?: string;
}

/**
 * AddDeal Component
 *
 * A complex form component for creating and editing energy deal contracts.
 * Manages multiple state variables for deal information including:
 * - Building, meter, and energy type selection
 * - Date range (from/to dates)
 * - Currency and price type selection
 * - Multiple price components (price, system, epad, profile, markup, commission)
 * - Visibility toggles based on price type
 *
 * Features:
 * - Form validation with error notifications
 * - LocalStorage caching of filter values
 * - Support for both creating new deals and editing existing ones
 * - Dynamic price column naming based on currency
 * - Automatic synchronization of quantity and share values
 *
 * @returns {ReactElement} The AddDeal form component
 */
export const AddDeal = (): ReactElement => {
  const navigate = useNavigate();
  const locale = useLang();
  let editableRow: DealRow = {};
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const localFilters: LocalFilters | null = JSON.parse(
    localStorage.getItem("addDealCache") || "{}",
  );

  const { user } = useUser() as { user: User | null };
  const {
    currentTeam,
    currentSubsidiary,
    currentTeamId,
    currentSubsidiaryId,
    countryCode: clientCountry,
  } = user || {};
  const priceTypes: PriceType[] = usePriceTypes({ countryCode: clientCountry });
  const currentDate: Date = new Date();
  const { id } = useParams<{ id?: string }>();
  const { dealRow } = useDealById(id ? id : "");

  if (id && dealRow?.building) {
    editableRow = dealRow as DealRow;
  }

  const defaultValues: DefaultValues = {
    building: localFilters?.buildingValue ? localFilters.buildingValue : "all",
    meter: localFilters?.meterValue ? localFilters.meterValue : "all",
    energyType: localFilters?.energyTypeValue
      ? localFilters.energyTypeValue
      : "electricity",
    fromDate: convertDateToUserTimezone(
      user?.timezone,
      currentDate.getFullYear() + "-01-01",
    ) as unknown as Date,
    toDate: convertDateToUserTimezone(
      user?.timezone,
      currentDate.getFullYear() + "-12-31",
    ) as unknown as Date,
    currency: "dkk",
    mainPriceType: localFilters?.mainPriceTypeValue || "fixed-price",

    priceType: "fixed",
    pricePrice: 0,
    priceKwh: 0,
    priceShare: 100,

    systemType: "variable",
    systemPrice: 0,
    systemKwh: 0,
    systemShare: 100,

    epadType: "variable",
    epadPrice: 0,
    epadKwh: 0,
    epadShare: 100,

    profileType: "variable",
    profilePrice: 0,
    profileKwh: 0,
    profileShare: 100,

    markupPrice: 0,
    markupShare: 100,

    commissionPrice: 0,
    commissionShare: 100,

    priceColumn: gettext("Price (øre/kWh)"),
  };

  const selectedValues: DealRow = {
    ...defaultValues,
    ...editableRow,
  };

  // region Form default values [React Hooks]
  const [countryCode, setCountryCode] = useState<string>("DK");

  const [energyTypeValue, setEnergyTypeValue] = useState<string>(
    selectedValues.energyType || "electricity",
  );

  const [zoneValue, setZoneValue] = useState<string>(selectedValues.zone || "");

  const [buildingValue, setBuildingValue] = useState<string>(
    selectedValues.building || "all",
  );
  const [meterValue, setMeterValue] = useState<string>(
    selectedValues.meter || "all",
  );
  const [fromDateValue, setFromDateValue] = useState<Date>(
    selectedValues.fromDate as Date,
  );
  const [toDateValue, setToDateValue] = useState<Date>(
    selectedValues.toDate as Date,
  );
  const [currencyValue, setCurrencyValue] = useState<string>(
    selectedValues.currency || "dkk",
  );
  const [mainPriceTypeValue, setMainPriceTypeValue] = useState<string>(
    selectedValues.mainPriceType || "fixed-price",
  );

  const [priceTypeValue, setPriceTypeValue] = useState<string>(
    selectedValues.priceType || "fixed",
  );
  const [pricePriceValue, setPricePriceValue] = useState<string | number>(
    selectedValues.pricePrice || 0,
  );
  const [priceKwhValue, setPriceKwhValue] = useState<string | number>(
    selectedValues.priceKwh || 0,
  );
  const [priceShareValue, setPriceShareValue] = useState<string | number>(
    selectedValues.priceShare || 100,
  );

  const [systemTypeValue, setSystemTypeValue] = useState<string>(
    selectedValues.systemType || "variable",
  );
  const [systemPriceValue, setSystemPriceValue] = useState<string | number>(
    selectedValues.systemPrice || 0,
  );
  const [systemKwhValue, setSystemKwhValue] = useState<string | number>(
    selectedValues.systemKwh || 0,
  );
  const [systemShareValue, setSystemShareValue] = useState<string | number>(
    selectedValues.systemShare || 100,
  );

  const [epadTypeValue, setEpadTypeValue] = useState<string>(
    selectedValues.epadType || "variable",
  );
  const [epadPriceValue, setEpadPriceValue] = useState<string | number>(
    selectedValues.epadPrice || 0,
  );
  const [epadKwhValue, setEpadKwhValue] = useState<string | number>(
    selectedValues.epadKwh || 0,
  );
  const [epadShareValue, setEpadShareValue] = useState<string | number>(
    selectedValues.epadShare || 100,
  );

  const [profileTypeValue, setProfileTypeValue] = useState<string>(
    selectedValues.profileType || "variable",
  );
  const [profilePriceValue, setProfilePriceValue] = useState<string | number>(
    selectedValues.profilePrice || 0,
  );
  const [profileKwhValue, setProfileKwhValue] = useState<string | number>(
    selectedValues.profileKwh || 0,
  );
  const [profileShareValue, setProfileShareValue] = useState<string | number>(
    selectedValues.profileShare || 100,
  );

  const [markupPriceValue, setMarkupPriceValue] = useState<string | number>(
    selectedValues.markupPrice || 0,
  );
  const [markupShareValue, setMarkupShareValue] = useState<string | number>(
    selectedValues.markupShare || 100,
  );

  const [commissionPriceValue, setCommissionPriceValue] = useState<
    string | number
  >(selectedValues.commissionPrice || 0);
  const [commissionShareValue, setCommissionShareValue] = useState<
    string | number
  >(selectedValues.commissionShare || 100);

  const [priceColumnName, setPriceColumnName] = useState<string>(
    selectedValues.priceColumn || gettext("Price (øre/kWh)"),
  );

  const [showPrice, setShowPrice] = useState<boolean>(false);
  const [showSystem, setShowSystem] = useState<boolean>(false);
  const [showEpad, setShowEpad] = useState<boolean>(false);
  const [showProfile, setShowProfile] = useState<boolean>(false);

  const [showMarkup, setShowMarkup] = useState<boolean>(false);
  const [showCommission, setShowCommission] = useState<boolean>(false);
  // endregion default values

  const biddingZone = useBiddingZones(countryCode);

  // region userEffects [React Hooks]
  // useEffect((): void => {
  //   setCountryCode(clientCountry || "DK");
  // }, [clientCountry]);

  useEffect((): void => {
    localStorage.setItem(
      "addDealCache",
      JSON.stringify({
        buildingValue: buildingValue,
        meterValue: meterValue,
        energyTypeValue: energyTypeValue,
        fromDateValue: fromDateValue,
        toDateValue: toDateValue,
        currencyValue: currencyValue,
        mainPriceTypeValue: mainPriceTypeValue,
      }),
    );
  }, [
    meterValue,
    buildingValue,
    energyTypeValue,
    fromDateValue,
    toDateValue,
    currencyValue,
    mainPriceTypeValue,
  ]);

  useEffect((): void => {
    if (editableRow.zone) {
      setZoneValue(editableRow.zone);
    }
  }, [editableRow.zone]);

  useEffect((): void => {
    if (editableRow.building) {
      setBuildingValue(editableRow.building);
    }
  }, [editableRow.building]);

  useEffect((): void => {
    if (editableRow.meter) {
      setMeterValue(editableRow.meter);
    }
  }, [editableRow.meter]);

  useEffect((): void => {
    if (editableRow.energyType) {
      setEnergyTypeValue(editableRow.energyType);
    }
  }, [editableRow.energyType]);

  useEffect((): void => {
    if (editableRow.fromDate) {
      setFromDateValue(removeTimezone(editableRow.fromDate));
    }
  }, [editableRow.fromDate]);

  useEffect((): void => {
    if (editableRow.toDate) {
      setToDateValue(removeTimezone(editableRow.toDate));
    }
  }, [editableRow.toDate]);

  useEffect((): void => {
    if (editableRow.currency) {
      setCurrencyValue(editableRow.currency);
    }
  }, [editableRow.currency]);

  useEffect((): void => {
    if (editableRow.mainPriceType) {
      setMainPriceTypeValue(editableRow.mainPriceType);
    }
  }, [editableRow.mainPriceType]);

  useEffect((): void => {
    if (editableRow.priceType) {
      setPriceTypeValue(editableRow.priceType);
    }
  }, [editableRow.priceType]);

  useEffect((): void => {
    if (editableRow.pricePrice) {
      setPricePriceValue(editableRow.pricePrice);
    }
  }, [editableRow.pricePrice]);

  useEffect((): void => {
    if (editableRow.priceKwh) {
      setPriceKwhValue(editableRow.priceKwh);
    }
  }, [editableRow.priceKwh]);

  useEffect((): void => {
    if (editableRow.priceShare) {
      setPriceShareValue(editableRow.priceShare);
    }
  }, [editableRow.priceShare]);

  useEffect((): void => {
    if (editableRow.systemType) {
      setSystemTypeValue(editableRow.systemType);
    }
  }, [editableRow.systemType]);

  useEffect((): void => {
    if (editableRow.systemPrice) {
      setSystemPriceValue(editableRow.systemPrice);
    }
  }, [editableRow.systemPrice]);

  useEffect((): void => {
    if (editableRow.systemKwh) {
      setSystemKwhValue(editableRow.systemKwh);
    }
  }, [editableRow.systemKwh]);

  useEffect((): void => {
    if (editableRow.systemShare) {
      setSystemShareValue(editableRow.systemShare);
    }
  }, [editableRow.systemShare]);

  useEffect((): void => {
    if (editableRow.epadType) {
      setEpadTypeValue(editableRow.epadType);
    }
  }, [editableRow.epadType]);

  useEffect((): void => {
    if (editableRow.epadPrice) {
      setEpadPriceValue(editableRow.epadPrice);
    }
  }, [editableRow.epadPrice]);

  useEffect((): void => {
    if (editableRow.epadKwh) {
      setEpadKwhValue(editableRow.epadKwh);
    }
  }, [editableRow.epadKwh]);

  useEffect((): void => {
    if (editableRow.epadShare) {
      setEpadShareValue(editableRow.epadShare);
    }
  }, [editableRow.epadShare]);

  useEffect((): void => {
    if (editableRow.profileType) {
      setProfileTypeValue(editableRow.profileType);
    }
  }, [editableRow.profileType]);

  useEffect((): void => {
    if (editableRow.profilePrice) {
      setProfilePriceValue(editableRow.profilePrice);
    }
  }, [editableRow.profilePrice]);

  useEffect((): void => {
    if (editableRow.profileKwh) {
      setProfileKwhValue(editableRow.profileKwh);
    }
  }, [editableRow.profileKwh]);

  useEffect((): void => {
    if (editableRow.profileShare) {
      setProfileShareValue(editableRow.profileShare);
    }
  }, [editableRow.profileShare]);

  useEffect((): void => {
    if (editableRow.markupPrice) {
      setMarkupPriceValue(editableRow.markupPrice);
    }
  }, [editableRow.markupPrice]);

  useEffect((): void => {
    if (editableRow.markupShare) {
      setMarkupShareValue(editableRow.markupShare);
    }
  }, [editableRow.markupShare]);

  useEffect((): void => {
    if (editableRow.commissionPrice) {
      setCommissionPriceValue(editableRow.commissionPrice);
    }
  }, [editableRow.commissionPrice]);

  useEffect((): void => {
    if (editableRow.commissionShare) {
      setCommissionShareValue(editableRow.commissionShare);
    }
  }, [editableRow.commissionShare]);

  useEffect((): void => {
    let effectiveType = mainPriceTypeValue;
    if (!effectiveType && priceTypes.length > 0) {
      effectiveType = localFilters?.mainPriceTypeValue || priceTypes[0].value;
      setMainPriceTypeValue(effectiveType);
    }

    const selected = priceTypes.find(
      (pt: PriceType) => pt.value === effectiveType,
    );
    setShowPrice(selected?.price || false);

    setShowSystem(selected?.system || false);

    setShowEpad(selected?.epad || false);

    setShowProfile(selected?.profile || false);

    setShowMarkup(selected?.markup || false);

    setShowCommission(selected?.commission || false);

    setPriceTypeValue(selected?.price ? "fixed" : "variable");

    setSystemTypeValue(selected?.system ? "fixed" : "variable");

    setEpadTypeValue(selected?.epad ? "fixed" : "variable");

    setProfileTypeValue(selected?.profile ? "fixed" : "variable");
  }, [mainPriceTypeValue, priceTypes, localFilters?.mainPriceTypeValue]);

  // endregion useEffects

  // region Form actions
  const resetForm = useCallback((): void => {
    setMainPriceTypeValue(defaultValues.mainPriceType);
    setPriceTypeValue(defaultValues.priceType);
    setPricePriceValue(defaultValues.pricePrice);
    setPriceKwhValue(defaultValues.priceKwh);
    setPriceShareValue(defaultValues.priceShare);

    setSystemTypeValue(defaultValues.systemType);
    setSystemPriceValue(defaultValues.systemPrice);
    setSystemKwhValue(defaultValues.systemKwh);
    setSystemShareValue(defaultValues.systemShare);

    setEpadTypeValue(defaultValues.epadType);
    setEpadPriceValue(defaultValues.epadPrice);
    setEpadKwhValue(defaultValues.epadKwh);
    setEpadShareValue(defaultValues.epadShare);

    setProfileTypeValue(defaultValues.profileType);
    setProfilePriceValue(defaultValues.profilePrice);
    setProfileKwhValue(defaultValues.profileKwh);
    setProfileShareValue(defaultValues.profileShare);

    setMarkupPriceValue(defaultValues.markupPrice);
    setMarkupShareValue(defaultValues.markupShare);

    setCommissionPriceValue(defaultValues.commissionPrice);
    setCommissionShareValue(defaultValues.commissionShare);
  }, [
    defaultValues.commissionPrice,
    defaultValues.commissionShare,
    defaultValues.mainPriceType,
    defaultValues.epadKwh,
    defaultValues.epadPrice,
    defaultValues.epadShare,
    defaultValues.epadType,
    defaultValues.markupPrice,
    defaultValues.markupShare,
    defaultValues.priceKwh,
    defaultValues.pricePrice,
    defaultValues.priceShare,
    defaultValues.priceType,
    defaultValues.profileKwh,
    defaultValues.profilePrice,
    defaultValues.profileShare,
    defaultValues.profileType,
    defaultValues.systemKwh,
    defaultValues.systemPrice,
    defaultValues.systemShare,
    defaultValues.systemType,
  ]);

  const { addDeal, loading: updating } = useAddDeal(
    (data: any): void => {
      if (!data?.addEnergyDeal?.success) {
        enqueueSnackbar(
          data?.addEnergyDeal?.message?.join("\n") ||
            gettext("Error: Something went wrong!"),
          {
            variant: "error",
          },
        );
        return;
      }
      enqueueSnackbar(gettext("The new deal has added"), {
        variant: "success",
      });
      resetForm();
      navigate("/energy-contract");
    },
    ((error: any): void => {
      if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });

        const fields: Array<{ title?: string; value?: string }> = [];

        const slackErrorData: SlackErrorData = {
          channel: SLACK_CHANNELS.FRONTEND_ERRORS,
          type: "error",
          heading: `Runtime error on Add Deal saving`,
          detail: `${(error as any)?.message || (error as any)?.toString() || String(error)}`,
          fields: fields,
        };
        sendSlackMessage(slackErrorData).then();
      }
    }) as any,
  );

  const { editDeal } = useEditDeal(
    (_data): void => {
      enqueueSnackbar(gettext("The deal has updated"), {
        variant: "success",
      });
      resetForm();
      navigate("/energy-contract");
    },
    ((error: any): void => {
      if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(error as string, {
          variant: "error",
        });

        const fields: Array<{ title?: string; value?: string }> = [];

        const slackErrorData: SlackErrorData = {
          channel: SLACK_CHANNELS.FRONTEND_ERRORS,
          type: "error",
          heading: `Runtime error on Edit Deal saving `,
          detail: `${(error as any)?.message || (error as any)?.toString() || String(error)}`,
          fields: fields,
        };
        sendSlackMessage(slackErrorData).then();
      }
    }) as any,
  );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (!validateSubmit()) {
      return;
    }

    const variables: DealVariables = {
      country: countryCode,
      zone: zoneValue,
      customer: currentTeam,
      subsidiary: currentSubsidiary,

      building: buildingValue,
      meter: meterValue || 0,
      energyType: energyTypeValue || "electricity",
      fromDate: formatDateForAPI(fromDateValue, "00:00:00"),
      toDate: formatDateForAPI(toDateValue, "23:59:59"),
      currency: currencyValue,
      mainPriceType: mainPriceTypeValue,

      priceType: priceTypeValue,
      pricePrice:
        parseLocaleNumber(pricePriceValue, locale) ||
        parseLocaleNumber(systemPriceValue, locale) ||
        0,
      priceKwh:
        parseLocaleNumber(priceKwhValue, locale) ||
        parseLocaleNumber(systemKwhValue, locale) ||
        0,
      priceShare:
        parseLocaleNumber(priceShareValue, locale) ||
        parseLocaleNumber(systemShareValue, locale) ||
        0,

      systemType: systemTypeValue,
      systemPrice:
        parseLocaleNumber(systemPriceValue, locale) ||
        parseLocaleNumber(pricePriceValue, locale) ||
        0,
      systemKwh:
        parseLocaleNumber(systemKwhValue, locale) ||
        parseLocaleNumber(priceKwhValue, locale) ||
        0,
      systemShare:
        parseLocaleNumber(systemShareValue, locale) ||
        parseLocaleNumber(systemShareValue, locale) ||
        0,

      epadType: epadTypeValue,
      epadPrice: parseLocaleNumber(epadPriceValue, locale) || 0,
      epadKwh: parseLocaleNumber(epadKwhValue, locale) || 0,
      epadShare: parseLocaleNumber(epadShareValue, locale) || 0,

      profileType: profileTypeValue,
      profilePrice: parseLocaleNumber(profilePriceValue, locale) || 0,
      profileKwh: parseLocaleNumber(profileKwhValue, locale) || 0,
      profileShare: parseLocaleNumber(profileShareValue, locale) || 0,

      markupPrice: parseLocaleNumber(markupPriceValue, locale) || 0,
      markupShare: parseLocaleNumber(markupShareValue, locale) || 0,

      commissionPrice: parseLocaleNumber(commissionPriceValue, locale) || 0,
      commissionShare: parseLocaleNumber(commissionShareValue, locale) || 0,
    };

    if (editableRow.Id) {
      variables.id = editableRow.Id;
      editDeal({
        variables: variables,
        refetchQueries: [{ query: GET_DEALS_LIST }],
      }).then();
    } else {
      addDeal({
        variables: variables,
        refetchQueries: [{ query: GET_DEALS_LIST }],
      }).then();
    }
  };

  const validateSubmit = (): boolean => {
    let returnValue: boolean = true;

    if (!buildingValue) {
      enqueueSnackbar(gettext("Building is required"), {
        variant: "error",
      });
      returnValue = false;
    }

    if (!meterValue) {
      enqueueSnackbar(gettext("Installation number is required"), {
        variant: "error",
      });
      returnValue = false;
    }

    if (!energyTypeValue) {
      enqueueSnackbar(gettext("Energy type is required"), {
        variant: "error",
      });
      returnValue = false;
    }

    if (!fromDateValue) {
      enqueueSnackbar(gettext("From date is required"), {
        variant: "error",
      });
      returnValue = false;
    }

    if (!toDateValue) {
      enqueueSnackbar(gettext("To date is required"), {
        variant: "error",
      });
      returnValue = false;
    }

    if (!currencyValue) {
      enqueueSnackbar(gettext("Currency is required"), {
        variant: "error",
      });
      returnValue = false;
    }

    if (!mainPriceTypeValue) {
      enqueueSnackbar(gettext("Price type is required"), {
        variant: "error",
      });
      returnValue = false;
    }

    return returnValue;
  };

  // region Value Change Filters
  const buildingValueChange = (building: { value: string }): void => {
    if (building.value !== buildingValue) {
      setBuildingValue(building.value);
    }
  };

  const meterValueChange = (selected: { value: string }): void => {
    setMeterValue(selected.value);
  };

  const energyTypeValueChange = (selected: { value: string }): void => {
    setEnergyTypeValue(selected.value);
  };

  const validDateValueChangeStart = (event: Date): void => {
    setFromDateValue(event);
  };

  const validDateValueChangeEnd = (event: Date): void => {
    setToDateValue(event);
  };

  const currencyValueChange = (selected: { value: string }): void => {
    setCurrencyValue(selected.value);
    switch (selected.value) {
      case "gbp":
        setPriceColumnName(gettext("Price (pence/kWh)"));
        break;
      case "usd":
      case "eur":
        setPriceColumnName(gettext("Price (cent/kWh)"));
        break;
      case "sek":
      case "nok":
      case "dkk":
      default:
        setPriceColumnName(gettext("Price (øre/kWh)"));
        break;
    }
  };

  const mainPriceTypeValueChange = (selected: any): void => {
    if (selected && typeof selected === "object" && "value" in selected) {
      setMainPriceTypeValue(selected.value);
    } else if (typeof selected === "string") {
      setMainPriceTypeValue(selected);
    }
  };
  // endregion Value Change Filters

  // region Value Change Price Row
  const pricePriceValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setPricePriceValue(event.target.value);
  };

  const priceKwhValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setPriceKwhValue(event.target.value);
    updateAllQuantity(parseLocaleNumber(event.target.value, locale) || 0);
  };

  const priceShareValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setPriceShareValue(event.target.value);
    updateAllShare(parseLocaleNumber(event.target.value, locale) || 0);
  };
  // endregion Value Change Price Row

  // region Value Change System Row
  const systemPriceValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSystemPriceValue(event.target.value);
  };

  const systemKwhValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSystemKwhValue(event.target.value);
    updateAllQuantity(parseLocaleNumber(event.target.value, locale) || 0);
  };

  const systemShareValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSystemShareValue(event.target.value);
    updateAllShare(parseLocaleNumber(event.target.value, locale) || 0);
  };
  // endregion Value Change System Row

  // region Value Change Epad
  const epadPriceValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setEpadPriceValue(event.target.value);
  };

  const epadKwhValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setEpadKwhValue(event.target.value);
    updateAllQuantity(parseLocaleNumber(event.target.value, locale) || 0);
  };

  const epadShareValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setEpadShareValue(event.target.value);
    updateAllShare(parseLocaleNumber(event.target.value, locale) || 0);
  };
  // endregion Value Change Epad

  // region Value Change Profile
  const profilePriceValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setProfilePriceValue(event.target.value);
  };

  const profileKwhValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setProfileKwhValue(event.target.value);
    updateAllQuantity(parseLocaleNumber(event.target.value, locale) || 0);
  };

  const profileShareValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setProfileShareValue(event.target.value);
    updateAllShare(parseLocaleNumber(event.target.value, locale) || 0);
  };
  // endregion Value Change Profile

  // region Value Change Markup Row
  const markupPriceValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setMarkupPriceValue(event.target.value);
  };
  // endregion Value Change Markup Row

  // region Value Change Commission Row
  const commissionPriceValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setCommissionPriceValue(event.target.value);
  };
  // endregion Value Change Commission Row

  // region Update Common Fields
  const updateAllShare = (value: number): void => {
    let shareValue: number = value;
    if (shareValue > 100) shareValue = 100;
    if (shareValue < 0) shareValue = 0;
    if (shareValue < 100) {
      updateAllQuantity(0);
    }
    setPriceShareValue(shareValue);
    setSystemShareValue(shareValue);
    setEpadShareValue(shareValue);
    setProfileShareValue(shareValue);
  };

  const updateAllQuantity = (value: number): void => {
    let quantityValue: number = value;
    if (quantityValue < 0) quantityValue = 0;
    if (quantityValue > 0) {
      updateAllShare(100);
    }
    setPriceKwhValue(quantityValue);
    setSystemKwhValue(quantityValue);
    setEpadKwhValue(quantityValue);
    setProfileKwhValue(quantityValue);
  };
  // endregion UpdateCommon Fields

  const handleCountryChange = (event: any) => {
    if (event.value) {
      setCountryCode(event.value);
    } else if (event) {
      setCountryCode(event);
    }
  };

  const handleZoneChange = (event: any) => {
    if (event.value) {
      setZoneValue(event.value);
    } else if (event) {
      setZoneValue(event);
    }
  };

  // endregion Change handlers

  return (
    <Box
      sx={{
        width: "auto",
      }}
    >
      <form onSubmit={submitHandler}>
        <Stack
          spacing={3}
          sx={{
            height: "100%",
          }}
        >
          <Grid
            container
            spacing={0}
            sx={{
              width: "100%",
            }}
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="h4">
                {!selectedValues.Id && gettext("Create New Contract")}
                {selectedValues.Id && gettext("Edit Contract")}
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 6 }}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                to={"/energy-contract"}
                component={Link}
              >
                {gettext("Contract list")}
              </Button>
            </Grid>
          </Grid>
          <Box
            sx={{
              height: "100%",
            }}
          >
            <FilterBox>
              <Grid
                container
                spacing={3}
                sx={{
                  width: "100%",
                }}
              >
                {/* Customer Country Dropdown Start */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <CustomersCountryDropdown
                    defaultValue={countryCode}
                    required
                    customerId={currentTeamId}
                    subsidiaryId={currentSubsidiaryId}
                    onChange={handleCountryChange}
                  />
                </Grid>
                {/* Customer Country Dropdown End */}
                {/* Energy Type Dropdown Start */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <EnergyTypeDropdown
                    onChange={energyTypeValueChange}
                    defaultValue={energyTypeValue}
                    currentTeam={currentTeam}
                    currentSubsidiary={currentSubsidiary}
                    country={countryCode}
                    showAll={false}
                  />
                </Grid>
                {/* Energy Type Dropdown End */}

                {/* Energy Type Dropdown Start */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <CommonDropdown
                    componentLabel={gettext("Bidding Zone")}
                    options={biddingZone}
                    onChange={handleZoneChange}
                    defaultValue={zoneValue}
                    required
                    showAll
                  />
                </Grid>
                {/* Energy Type Dropdown End */}

                {/* Building Dropdown Start */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <BuildingDropdown
                    onChange={buildingValueChange}
                    defaultValue={buildingValue}
                    country={countryCode}
                    energyType={energyTypeValue}
                    zone={zoneValue}
                    showAll={true}
                  />
                </Grid>
                {/* Building Dropdown End */}

                {/* Meter Dropdown Start */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <MeterDropdown
                    onChange={meterValueChange}
                    energyType={energyTypeValue}
                    building={buildingValue}
                    country={countryCode}
                    zone={zoneValue}
                    defaultValue={meterValue}
                    showAll={true}
                    visualStyle="autoComplete"
                  />
                </Grid>
                {/* Meter Dropdown End */}

                {/* region Date Picker (From) Start */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <SingleDatePicker
                    componentLabel={gettext("Start Date")}
                    selectedDate={fromDateValue.toString()}
                    user={user}
                    onValueChange={validDateValueChangeStart}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <SingleDatePicker
                    componentLabel={gettext("End Date")}
                    selectedDate={toDateValue.toString()}
                    user={user}
                    onValueChange={validDateValueChangeEnd}
                  />
                </Grid>
                {/* endregion Date Picker */}

                {/* Currency Dropdown Start */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <CurrencyDropdown
                    defaultValue={currencyValue}
                    onChange={currencyValueChange}
                    showAll={false}
                  />
                </Grid>
                {/* Currency Dropdown End */}

                {/* Price Type Dropdown Start */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <PriceTypeDropdownSimple
                    countryCode={countryCode}
                    onChange={mainPriceTypeValueChange}
                    defaultValue={mainPriceTypeValue}
                    showAll={false}
                  />
                </Grid>
                {/* Price Type Dropdown End */}
              </Grid>
            </FilterBox>
            <Grid sx={{ marginTop: 2 }}></Grid>
            <TableBox
              sx={{
                p: 2,
              }}
            >
              <Grid
                container
                spacing={2}
                sx={{
                  width: "100%",
                }}
              >
                {showPrice && (
                  <>
                    <Grid
                      size={{
                        xs: 12,
                      }}
                      sx={{
                        marginTop: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "700",
                        }}
                      >
                        {gettext("Fixed Price")}
                      </Typography>
                    </Grid>
                    <Grid
                      container
                      spacing={3}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={priceColumnName}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={pricePriceValue}
                          onChange={pricePriceValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={gettext("Volume (kWh)")}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={priceKwhValue}
                          onChange={priceKwhValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={gettext("Share (%)")}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={priceShareValue}
                          onChange={priceShareValueChange}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {showSystem && (
                  <>
                    <Grid
                      size={{
                        xs: 12,
                      }}
                      sx={{
                        marginTop: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "700",
                        }}
                      >
                        {gettext("System Price")}
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      spacing={3}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={priceColumnName}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={systemPriceValue}
                          onChange={systemPriceValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={gettext("Volume (kWh)")}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={systemKwhValue}
                          onChange={systemKwhValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={gettext("Share (%)")}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={systemShareValue}
                          onChange={systemShareValueChange}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {showEpad && (
                  <>
                    <Grid
                      size={{
                        xs: 12,
                      }}
                      sx={{
                        marginTop: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "700",
                        }}
                      >
                        {gettext("EPAD")}
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      spacing={3}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={priceColumnName}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={epadPriceValue}
                          onChange={epadPriceValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={gettext("Volume (kWh)")}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={epadKwhValue}
                          onChange={epadKwhValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={gettext("Share (%)")}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={epadShareValue}
                          onChange={epadShareValueChange}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {showProfile && (
                  <>
                    <Grid
                      size={{
                        xs: 12,
                      }}
                      sx={{
                        marginTop: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "700",
                        }}
                      >
                        {gettext("Profile")}
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      spacing={3}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={priceColumnName}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={profilePriceValue}
                          onChange={profilePriceValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={gettext("Volume (kWh)")}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={profileKwhValue}
                          onChange={profileKwhValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={gettext("Share (%)")}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={profileShareValue}
                          onChange={profileShareValueChange}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {showMarkup && (
                  <>
                    <Grid
                      size={{
                        xs: 12,
                      }}
                      sx={{
                        marginTop: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "700",
                        }}
                      >
                        {gettext("Markup")}
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      spacing={3}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={priceColumnName}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={markupPriceValue}
                          onChange={markupPriceValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label={gettext("Volume (kWh)")}
                          fullWidth
                          disabled
                          value={gettext("All kWh")}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label={gettext("Share (%)")}
                          fullWidth
                          disabled
                          value={gettext("Not Applicable")}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {showCommission && (
                  <>
                    <Grid
                      size={{
                        xs: 12,
                      }}
                      sx={{
                        marginTop: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "700",
                        }}
                      >
                        {gettext("Spot premium")}
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      spacing={3}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LocaleNumberTextField
                          label={priceColumnName}
                          placeholder={gettext("0.00")}
                          fullWidth
                          value={commissionPriceValue}
                          onChange={commissionPriceValueChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label={gettext("Volume (kWh)")}
                          fullWidth
                          disabled
                          value={gettext("All kWh")}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label={gettext("Share (%)")}
                          fullWidth
                          disabled
                          value={gettext("Not Applicable")}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </TableBox>
          </Box>
          <Paper
            elevation={0}
            sx={{
              textAlign: "right",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                margin: "5px",
                minWidth: 164,
                maxWidth: "100%",
              }}
              component={Link}
              to={"/energy-contract"}
            >
              {gettext("Cancel")}
            </Button>
            <Button
              variant="contained"
              loading={updating}
              type="submit"
              sx={{
                margin: "5px",
                minWidth: 164,
                maxWidth: "100%",
              }}
            >
              {editableRow.Id ? gettext("Update") : gettext("Add")}
            </Button>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};
